import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import "./Footer.css";
import { fetchContentDocuments } from "../api/contentful";
import { ReactComponent as Logo } from "../assets/images/logo/LOGO.svg";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { formatPhoneNumber } from "./dashboard/utils/PhoneFormatters";

const FOOTER_EXPLORE_LINKS = [
  { label: "Home", to: "/" },
  { label: "The project", to: "/about-project" },
  { label: "CSA", to: "/csa" },
  { label: "Events", to: "/events" },
  { label: "Publications", to: "/publications" },
  { label: "Gallery", to: "/gallery" },
  { label: "Sponsors", to: "/sponsors" },
] as const;

const FOOTER_MORE_LINKS = [
  { label: "Join us", to: "/join" },
  { label: "CSA signup", to: "/csa-signup" },
  { label: "Memberships", to: "/memberships" },
  { label: "Support us", to: "/support-us" },
  { label: "Education", to: "/education" },
  { label: "Tours & lectures", to: "/education/tours-visits-and-lectures" },
  { label: "Internships", to: "/education/internships" },
] as const;

/** Must match the Contentful `documents` entry title exactly. */
const PRIVACY_POLICY_DOCUMENT_TITLE = "Privacy Policy (GDPR)";

type FooterLink = { label: string; to: string };

const FooterLinkColumn = ({
  heading,
  links,
  ariaLabel,
}: {
  heading: string;
  links: readonly FooterLink[];
  ariaLabel: string;
}) => (
  <nav className="site-footer__nav" aria-label={ariaLabel}>
    <h3 className="site-footer__heading">{heading}</h3>
    <ul className="site-footer__link-list">
      {links.map(({ label, to }) => (
        <li key={to}>
          <Link className="site-footer__link" to={to}>
            {label}
          </Link>
        </li>
      ))}
    </ul>
  </nav>
);

const Footer: React.FC = () => {
  const { data: companyDetails } = useCompanyDetails();
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const companyName =
    companyDetails?.companyName?.trim() || "Alnarp's Agroecology Farm";
  const email = companyDetails?.companyEmailAddress?.trim();
  const phone = companyDetails?.primaryPhoneNumber?.trim();

  useEffect(() => {
    let cancelled = false;

    const fetchPrivacyPolicy = async () => {
      try {
        const { items: documentItems } = await fetchContentDocuments();

        const privacyPolicy = documentItems.find(
          (document) =>
            document.fields?.title === PRIVACY_POLICY_DOCUMENT_TITLE,
        );

        const rawUrl = privacyPolicy?.fields?.file?.fields?.file?.url ?? "";
        if (!rawUrl || cancelled) return;

        setPrivacyPolicyUrl(
          rawUrl.startsWith("//") ? `https:${rawUrl}` : rawUrl,
        );
      } catch (error) {
        console.error("Failed to load privacy policy document:", error);
      }
    };

    fetchPrivacyPolicy();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__grid">
          <section className="site-footer__brand" aria-label="Contact">
            <h2 className="site-footer__title">{companyName}</h2>

            <div className="site-footer__brand-body">
              <ul className="site-footer__contact-list">
                {email && (
                  <li>
                    <a
                      className="site-footer__contact-link"
                      href={`mailto:${email}`}
                    >
                      <FontAwesomeIcon icon={faEnvelope} aria-hidden />
                      <span>{email}</span>
                    </a>
                  </li>
                )}
                {phone && (
                  <li>
                    <a
                      className="site-footer__contact-link"
                      href={`tel:${phone}`}
                    >
                      <FontAwesomeIcon icon={faPhone} aria-hidden />
                      <span>{formatPhoneNumber(phone)}</span>
                    </a>
                  </li>
                )}
              </ul>

              <div className="site-footer__socials">
                {companyDetails?.instagramLink && (
                  <a
                    href={companyDetails.instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="site-footer__social-link"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                )}
                {companyDetails?.facebookLink && (
                  <a
                    href={companyDetails.facebookLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="site-footer__social-link"
                  >
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                )}
                {companyDetails?.linkedinLink && (
                  <a
                    href={companyDetails.linkedinLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="site-footer__social-link"
                  >
                    <FontAwesomeIcon icon={faLinkedin} />
                  </a>
                )}
              </div>

              <div className="site-footer__logo">
                <Link to="/" aria-label="Home">
                  <Logo />
                </Link>
              </div>
            </div>
          </section>

          <FooterLinkColumn
            heading="Explore"
            links={FOOTER_EXPLORE_LINKS}
            ariaLabel="Explore"
          />

          <FooterLinkColumn
            heading="Get involved"
            links={FOOTER_MORE_LINKS}
            ariaLabel="Get involved"
          />
        </div>

        <div className="site-footer__bottom">
          {privacyPolicyUrl ? (
            <a
              className="site-footer__legal-link"
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy policy
            </a>
          ) : (
            <span className="site-footer__legal-link">Privacy policy</span>
          )}{" "}
          <p className="site-footer__copyright">
            &copy; {new Date().getFullYear()} {companyName}
          </p>
          <p className="site-footer__copyright">
            Developed by{" "}
            <a
              className="site-footer__copyright"
              href="https://www.linkedin.com/in/daisy-holm-57b845227/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Daisy Holm
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
